import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/shared/hooks/use-toast';
import { ForgotPasswordForm } from './ForgotPasswordForm';
import { useI18n } from '@/contexts/I18nContext';
import { Button } from '@/components/ui/button';
import { GoogleAuthButton } from './GoogleAuthButton';

interface SignInFormProps {
  onEmailSent?: (message: string) => void;
  onCreateAccount?: () => void;
}

export const SignInForm = ({ onEmailSent, onCreateAccount }: SignInFormProps) => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [touched, setTouched] = useState({ email: false, password: false });

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field as keyof typeof touched]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmailBlur = () => {
    setTouched(prev => ({ ...prev, email: true }));
    const email = formData.email.trim();
    if (email !== formData.email) {
      setFormData(prev => ({ ...prev, email }));
    }
    if (!email) {
      setErrors(prev => ({ ...prev, email: t('auth.sign_in_form.email_required') }));
    } else if (!validateEmail(email)) {
      setErrors(prev => ({ ...prev, email: t('auth.sign_in_form.email_invalid') }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePasswordBlur = () => {
    setTouched(prev => ({ ...prev, password: true }));
    if (!formData.password) {
      setErrors(prev => ({ ...prev, password: t('auth.sign_in_form.password_validation') }));
    } else {
      setErrors(prev => ({ ...prev, password: '' }));
    }
  };

  const isFormValid = () => {
    const email = formData.email.trim();
    return email && formData.password && validateEmail(email) && !errors.email && !errors.password;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { error } = await signIn(formData.email.trim(), formData.password);
      if (!error) {
        const returnTo = sessionStorage.getItem('auth_return_to');
        sessionStorage.removeItem('auth_return_to');
        navigate(returnTo || '/home', { replace: true });
      }
    } catch {
      toast({ title: t('auth.error'), description: t('auth.unexpected_error'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={() => setShowForgotPassword(false)} />;
  }

  return (
    <div className="w-full space-y-6">
      {/* Prominent heading + tagline */}
      <div className="mb-1">
        <h2 className="text-[28px] font-extrabold leading-tight tracking-tight text-white">
          {t('auth.welcome_back') || 'Bem-vindo de volta'}
        </h2>
        <p className="mt-1 text-[15px] text-white/60">
          {t('auth.sign_in_tagline') || 'Oss, bem-vindo de volta ao tatame'}
        </p>
      </div>

      {/* Social sign-in */}
      <GoogleAuthButton label={t('auth.google.signin')} />

      {/* Divider */}
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-white/10" />
        <span className="text-xs text-white/40">{t('auth.google.or')}</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email */}
        <div className="space-y-1">
          <label className="text-xs font-medium text-white/70">
            {t('auth.email') || 'E-mail'}
          </label>
          <Input
            type="email"
            autoComplete="email"
            placeholder={t('auth.sign_in_form.email_placeholder') || 'seu@email.com'}
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            onBlur={handleEmailBlur}
            showFloatingLabel={false}
            error={!!(errors.email && touched.email)}
          />
          {errors.email && touched.email && (
            <p className="text-xs text-red-300">{errors.email}</p>
          )}
        </div>

        {/* Password */}
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-white/70">
              {t('auth.sign_in_form.password_label') || 'Senha'}
            </label>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-xs text-sossiety-red hover:text-sossiety-red-hover transition-colors"
            >
              {t('auth.sign_in_form.forgot_password') || 'Esqueceu a senha?'}
            </button>
          </div>
          <PasswordInput
            autoComplete="current-password"
            placeholder={t('auth.sign_in_form.password_placeholder') || 'Sua senha'}
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            onBlur={handlePasswordBlur}
            error={!!(errors.password && touched.password)}
            showFloatingLabel={false}
          />
          {errors.password && touched.password && (
            <p className="text-xs text-red-300">{errors.password}</p>
          )}
        </div>

        {/* Submit */}
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={loading || !isFormValid()}
          className="w-full mt-2"
        >
          {loading ? t('auth.sign_in_form.signing_in') : t('auth.sign_in_form.sign_in_button') || 'Entrar'}
        </Button>
      </form>

      {/* Create account link */}
      {onCreateAccount && (
        <p className="text-center text-xs text-white/50">
          {t('auth.no_account') || 'Não tem conta?'}{' '}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCreateAccount}
            className="text-sossiety-red hover:text-sossiety-red-hover p-0 h-auto font-medium"
          >
            {t('auth.create_account') || 'Criar conta'}
          </Button>
        </p>
      )}

      {/* Terms disclaimer */}
      <p className="text-center text-[11px] text-white/30 leading-relaxed">
        {t('auth.terms_disclaimer')}{' '}
        <a href="/terms-of-use" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/50 transition-colors">
          {t('auth.terms_of_use')}
        </a>
        {' '}{t('auth.and_the')}{' '}
        <a href="/privacy-policies" target="_blank" rel="noopener noreferrer" className="underline hover:text-white/50 transition-colors">
          {t('auth.privacy_policy')}
        </a>.
      </p>
    </div>
  );
};
